import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Stack,
  Card,
  CardBody,
  CardHeader,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Flex,
  Divider,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  IconButton,
  HStack,
  VStack,
  Spinner,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Image,
} from '@chakra-ui/react';
import {
  ViewIcon,
  EditIcon,
  StarIcon,
  SettingsIcon,
  AddIcon,
  DeleteIcon
} from '@chakra-ui/icons';
import { resolveProductImageUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useProducts } from '../../contexts/ProductContext';
import { useNavigate } from 'react-router-dom';
import { Product } from '../../types/product';

const FarmerDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { updateProduct, deleteProduct, deactivateProduct, getProductsByFarmer } = useProducts();
  const [myProducts, setMyProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editImageFiles, setEditImageFiles] = useState<File[]>([]);
  const [editForm, setEditForm] = useState({
    name: '',
    category: '',
    price: '',
    quantity: '',
    unit: '',
    harvestDate: '',
    breed: '',
    age: '',
    description: '',
  });

  useEffect(() => {
    const loadMyProducts = async () => {
      if (!user) {
        console.log('No user available');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        console.log('Starting to fetch products for user:', user.id);
        const products = await getProductsByFarmer(user.id);
        console.log('Products received in dashboard:', products);
        setMyProducts(products);
      } catch (error) {
        console.error('Error loading products:', error);
        toast({
          title: 'Error loading products',
          description: 'Please try refreshing the page',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadMyProducts();
  }, [user, getProductsByFarmer, toast]);

  const quickActions = [
    {
      title: 'Add New Product',
      icon: ViewIcon,
      action: () => navigate('/dashboard/add-product'),
      description: 'List a new product for sale in the marketplace'
    },
    {
      title: 'Market Prices',
      icon: StarIcon,
      action: () => navigate('/market-prices'),
      description: 'View current market prices'
    }
  ];

  const handleEditProduct = (product: Product) => {
    if (!product._id) {
      toast({
        title: 'Error',
        description: 'Cannot edit product: ID is missing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setEditingProduct(product);
    setEditImageFiles([]);
    setEditForm({
      name: product.name,
      category: product.category,
      price: product.price.toString(),
      quantity: product.quantity.toString(),
      unit: product.unit,
      description: product.description,
      harvestDate: product.harvestDate
        ? new Date(product.harvestDate).toISOString().split('T')[0]
        : '',
      breed: product.breed || '',
      age: product.age != null ? product.age.toString() : '',
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingProduct || !editingProduct._id) return;

    try {
      const formData = new FormData();
      formData.append('name', editForm.name);
      formData.append('category', editForm.category);
      formData.append('price', String(parseFloat(editForm.price)));
      formData.append('quantity', String(parseInt(editForm.quantity)));
      formData.append('unit', editForm.unit);
      formData.append('description', editForm.description);

      if (editForm.category === 'livestock') {
        if (editForm.breed) formData.append('breed', editForm.breed);
        if (editForm.age) formData.append('age', editForm.age);
      } else {
        if (editForm.harvestDate) {
          formData.append('harvestDate', new Date(editForm.harvestDate).toISOString());
        }
      }

      editImageFiles.forEach(file => formData.append('images', file));

      await updateProduct(editingProduct._id, formData);

      // Refresh local list
      const refreshed = await getProductsByFarmer(user!.id);
      setMyProducts(refreshed);

      toast({
        title: 'Product Updated',
        description: 'Your product has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update the product. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        console.log('Starting product deletion:', productId);
        await deleteProduct(productId);
        console.log('Product deleted successfully');
        
        // Update local state by removing the deleted product
        setMyProducts(prev => {
          const updated = prev.filter(p => p._id !== productId);
          console.log('Updated products list:', updated);
          return updated;
        });
        
        toast({
          title: 'Product Deleted',
          description: 'Your product has been successfully removed.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        console.error('Error deleting product:', error);
        toast({
          title: 'Delete Failed',
          description: error.message || 'Failed to delete the product. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };
  const handleDeactivateProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to deactivate this product?')) {
      try {
        await deactivateProduct(productId);
        setMyProducts(prev =>
          prev.map(p => (p._id === productId ? { ...p, status: 'deactivated' } : p))
        );
        toast({
          title: 'Product Deactivated',
          description: 'Your product has been deactivated.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: 'Deactivation Failed',
          description: error.message || 'Failed to deactivate product.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };
  return (
    <>
      <Container maxW="container.xl" py={8}>
        {/* Welcome Section */}
        <Flex justify="space-between" align="center" mb={8}>
          <Box>
            <Heading size="lg" mb={2}>
              Welcome back, {user?.fullName}
            </Heading>
            <Text color="gray.600">
              Here's an overview of your farm and activities
            </Text>
          </Box>
          <Button
            colorScheme="brand"
            size="lg"
            leftIcon={<AddIcon />}
            onClick={() => navigate('/dashboard/add-product')}
          >
            Add New Product
          </Button>
        </Flex>

        <Grid templateColumns={{ base: 'repeat(1, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
          {/* Main Stats Section */}
          <GridItem colSpan={{ base: 1, lg: 2 }}>
            <Stack spacing={6}>
              {/* Farm Overview Card */}
              <Card>
                <CardHeader>
                  <Heading size="md">Farm Overview</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Stat>
                      <StatLabel>Farm Location</StatLabel>
                      <StatNumber>{user?.farmLocation || 'Not specified'}</StatNumber>
                      <StatHelpText>Your registered location</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Total Farm Size</StatLabel>
                      <StatNumber>{user?.farmSize || 0} hectares</StatNumber>
                      <StatHelpText>Registered farm area</StatHelpText>
                    </Stat>
                  </SimpleGrid>
                </CardBody>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <Heading size="md">Quick Actions</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {quickActions.map((action, index) => (
                      <Button
                        key={index}
                        leftIcon={<action.icon />}
                        onClick={action.action}
                        variant="outline"
                        justifyContent="flex-start"
                        height="auto"
                        py={4}
                        px={6}
                      >
                        <Box textAlign="left">
                          <Text fontWeight="bold">{action.title}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {action.description}
                          </Text>
                        </Box>
                      </Button>
                    ))}
                  </SimpleGrid>
                </CardBody>
              </Card>
            </Stack>
          </GridItem>

          {/* Right Sidebar */}
          <GridItem colSpan={1}>
            <Stack spacing={6}>
              {/* My Products Card */}
              <Card>
                <CardHeader pb={0}>
                  <Heading size="md">My Listed Products</Heading>
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                    <Box textAlign="center" py={4}>
                      <Spinner size="lg" />
                    </Box>
                  ) : (
                    <Tabs colorScheme="brand" isFitted variant="enclosed">
                      <TabList mb={4}>
                        <Tab fontSize="sm" fontWeight="600">
                          Active ({myProducts.filter(p => p.status !== 'deactivated').length})
                        </Tab>
                        <Tab fontSize="sm" fontWeight="600">
                          Deactivated ({myProducts.filter(p => p.status === 'deactivated').length})
                        </Tab>
                      </TabList>
                      <TabPanels>
                        {/* Active Listings Panel */}
                        <TabPanel px={0} py={2}>
                          <Stack spacing={4}>
                            {myProducts
                              .filter(product => product.status !== 'deactivated')
                              .map(product => (
                                <Box key={product._id || product.id} p={3} borderWidth="1px" borderRadius="md">
                                  <Flex justify="space-between" align="start">
                                    <Box>
                                      <HStack spacing={2} align="center" mb={1}>
                                        <Heading size="sm">{product.name}</Heading>
                                        <Badge colorScheme="green">Active</Badge>
                                      </HStack>
                                      <Text fontSize="sm" color="gray.600">
                                        Price: ${product.price}/{product.unit}
                                      </Text>
                                      <Text fontSize="sm" color="gray.600">
                                        Quantity: {product.quantity} {product.unit}
                                      </Text>
                                      <Text fontSize="xs" color="gray.400" mt={1}>
                                        Listed: {new Date(product.createdAt).toLocaleDateString()}
                                      </Text>
                                    </Box>
                                    <HStack spacing={1}>
                                      <IconButton
                                        aria-label="Edit product"
                                        icon={<EditIcon />}
                                        size="sm"
                                        colorScheme="blue"
                                        onClick={() => handleEditProduct(product)}
                                      />
                                      <Button
                                        size="xs"
                                        colorScheme="orange"
                                        onClick={() => {
                                          if (product._id) handleDeactivateProduct(product._id);
                                        }}
                                      >
                                        Deactivate
                                      </Button>
                                      <IconButton
                                        aria-label="Delete product"
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        colorScheme="red"
                                        onClick={() => {
                                          if (product._id) handleDeleteProduct(product._id);
                                        }}
                                      />
                                    </HStack>
                                  </Flex>
                                </Box>
                              ))}
                            {myProducts.filter(p => p.status !== 'deactivated').length === 0 && (
                              <Text color="gray.600">No active products listed.</Text>
                            )}
                          </Stack>
                        </TabPanel>

                        {/* Deactivated Listings Panel */}
                        <TabPanel px={0} py={2}>
                          <Stack spacing={4}>
                            {myProducts
                              .filter(product => product.status === 'deactivated')
                              .map(product => (
                                <Box key={product._id || product.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                                  <Flex justify="space-between" align="start">
                                    <Box>
                                      <HStack spacing={2} align="center" mb={1}>
                                        <Heading size="sm" color="gray.600">{product.name}</Heading>
                                        <Badge colorScheme="gray">Deactivated</Badge>
                                      </HStack>
                                      <Text fontSize="sm" color="gray.500">
                                        Price: ${product.price}/{product.unit}
                                      </Text>
                                      <Text fontSize="xs" color="gray.400" mt={1}>
                                        Listed: {new Date(product.createdAt).toLocaleDateString()}
                                      </Text>
                                    </Box>
                                    <IconButton
                                      aria-label="Delete product"
                                      icon={<DeleteIcon />}
                                      size="sm"
                                      colorScheme="red"
                                      onClick={() => {
                                        if (product._id) handleDeleteProduct(product._id);
                                      }}
                                    />
                                  </Flex>
                                </Box>
                              ))}
                            {myProducts.filter(p => p.status === 'deactivated').length === 0 && (
                              <Text color="gray.600">No deactivated products.</Text>
                            )}
                          </Stack>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  )}
                </CardBody>
              </Card>
            </Stack>
          </GridItem>
        </Grid>
      </Container>

      {/* Edit Product Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Product: {editingProduct?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* Current image preview */}
              {editingProduct?.imageUrl && (
                <Box w="full">
                  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">Current Image</Text>
                  <Image
                    src={resolveProductImageUrl(editingProduct.imageUrl)}
                    alt={editingProduct.name}
                    h="120px"
                    w="full"
                    objectFit="cover"
                    borderRadius="md"
                    fallbackSrc="/images/product-placeholder.jpg"
                  />
                </Box>
              )}

              <FormControl isRequired>
                <FormLabel>Product Name</FormLabel>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                >
                  <option value="grains">Grains</option>
                  <option value="vegetables">Vegetables</option>
                  <option value="fruits">Fruits</option>
                  <option value="livestock">Livestock</option>
                  <option value="dairy">Dairy</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Unit</FormLabel>
                <Select
                  value={editForm.unit}
                  onChange={(e) => setEditForm({...editForm, unit: e.target.value})}
                >
                  <option value="kg">Kilogram (kg)</option>
                  <option value="ton">Tonne (ton)</option>
                  <option value="bag">Bag</option>
                  <option value="crate">Crate</option>
                  <option value="piece">Piece</option>
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price (per {editForm.unit || editingProduct?.unit})</FormLabel>
                <Input
                  type="number"
                  value={editForm.price}
                  onChange={(e) => setEditForm({...editForm, price: e.target.value})}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Quantity ({editForm.unit || editingProduct?.unit})</FormLabel>
                <Input
                  type="number"
                  value={editForm.quantity}
                  onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Brand / Manufacturer</FormLabel>
                <Input
                  value={editForm.brand || ''}
                  onChange={(e) => setEditForm({...editForm, brand: e.target.value})}
                  placeholder="e.g. Honda, STIHL, Grundfos"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Condition</FormLabel>
                <Select
                  value={editForm.condition || 'new'}
                  onChange={(e) => setEditForm({...editForm, condition: e.target.value})}
                >
                  <option value="new">Brand New</option>
                  <option value="used">Used / Pre-owned</option>
                  <option value="refurbished">Refurbished</option>
                </Select>
              </FormControl>

              {editForm.category === 'livestock' ? (
                <>
                  <FormControl>
                    <FormLabel>Breed / Type</FormLabel>
                    <Input
                      value={editForm.breed}
                      onChange={(e) => setEditForm({...editForm, breed: e.target.value})}
                      placeholder="Enter breed or type"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Age (months)</FormLabel>
                    <Input
                      type="number"
                      value={editForm.age}
                      onChange={(e) => setEditForm({...editForm, age: e.target.value})}
                      placeholder="Enter age in months"
                    />
                  </FormControl>
                </>
              ) : (
                <FormControl>
                  <FormLabel>Harvest Date</FormLabel>
                  <Input
                    type="date"
                    value={editForm.harvestDate}
                    onChange={(e) => setEditForm({...editForm, harvestDate: e.target.value})}
                  />
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Update Product Images (Max 5)</FormLabel>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      setEditImageFiles(Array.from(e.target.files).slice(0, 5));
                    }
                  }}
                />
                {editImageFiles.length > 0 && (
                  <Text fontSize="xs" color="green.600" mt={1}>
                    {editImageFiles.length} new image{editImageFiles.length > 1 ? 's' : ''} selected
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="brand" mr={3} onClick={handleSaveEdit}>
              Save Changes
            </Button>
            <Button variant="ghost" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FarmerDashboard;
